import { freshPage, homeFor } from "../session";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { portalApi } from "../api";
import { codeOf, errorText } from "../errors";
import { useI18n } from "../i18n";
import { fragmentToken, stripFragment } from "../routes";
import { usePortal } from "../state";
import { Field, Link, Loading, Notice, PageTitle } from "../ui";
import { normaliseOrgNumber, normalisePostcode } from "@shared/org-number";
import {
  AUDIENCE_BANDS,
  bandLabel,
  planCell,
  type AudienceBand,
  type PlanMatrix,
} from "@shared/plan-matrix";

/**
 * Account entry points: log in, apply for an account (no open sign-up),
 * confirm, resend, forgot, reset, and a school invite.
 *
 * ## No enumeration, and the copy does not undo it
 *
 * Sign-up, resend and forgot answer `202 {ok:true}` whatever the address,
 * so each shows ONE message whatever the address, worded as "if the address
 * can be used, a mail is on its way". Login has one `invalid_credentials`.
 *
 * ## Tokens come from the fragment and go in the body
 *
 * `/account/confirm#token=…`, `/account/reset-password#token=…` and
 * `/invite#token=…`: the token is read once from the fragment, the fragment
 * is stripped from the address bar (and history) at once, and the token is
 * POSTed in a JSON body - never put in a path or a query that reaches a log.
 */

const MIN_PASSWORD = 10;

function passwordProblem(pw: string): "auth.tooShort" | null {
  return Array.from(pw).length < MIN_PASSWORD ? "auth.tooShort" : null;
}

/**
 * A coarse label for the devices list ("Firefox, Windows") - enough to tell
 * one's own sessions apart, without storing the full user-agent string.
 */
export function deviceLabel(ua: string): string {
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Firefox\//.test(ua)
      ? "Firefox"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Safari\//.test(ua)
          ? "Safari"
          : "";
  const os = /iPhone|iPad/.test(ua)
    ? "iOS"
    : /Android/.test(ua)
      ? "Android"
      : /Windows/.test(ua)
        ? "Windows"
        : /Mac OS X/.test(ua)
          ? "macOS"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return [browser, os].filter(Boolean).join(", ");
}

/** Read the fragment token once, then strip it. Survives StrictMode. */
function useFragmentToken(): string {
  const [token] = useState(() => fragmentToken());
  useEffect(() => {
    stripFragment();
  }, []);
  return token;
}

export function LoginScreen({ next }: { next: string | null }) {
  const { t } = useI18n();
  const { setMe, loginEmail, me } = usePortal();
  const [email, setEmail] = useState(loginEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setUnverified(false);
    setBusy(true);
    try {
      const res = await portalApi.login({
        email: email.trim(),
        password,
        deviceName: deviceLabel(navigator.userAgent) || undefined,
      });
      setMe(res.account);
      // A fresh page: nothing of whoever was here before survives.
      freshPage(homeFor(res.account.kind, next));
    } catch (err) {
      setBusy(false);
      setUnverified(codeOf(err) === "email_not_verified");
      setError(errorText(err, t));
    }
  };

  // Already signed in: say as whom, and offer to switch - never a silent
  // redirect into someone else's account.
  if (me) {
    return (
      <section className="panel panel--narrow">
        <PageTitle>{t("auth.loginTitle")}</PageTitle>
        <SignedInAs
          email={me.email}
          continueTo={homeFor(me.kind, next)}
          switchTo={`/login${next ? `?next=${encodeURIComponent(next)}` : ""}`}
        />
      </section>
    );
  }

  return (
    <section className="panel panel--narrow">
      <PageTitle>{t("auth.loginTitle")}</PageTitle>
      <form onSubmit={(e) => void submit(e)}>
        <Field
          label={t("common.email")}
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Field
          label={t("common.password")}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        {error && <Notice kind="error">{error}</Notice>}
        {unverified && (
          <p>
            <Link to="/account/resend">{t("auth.resend")}</Link>
          </p>
        )}
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? t("auth.loggingIn") : t("auth.login")}
        </button>
      </form>
      <p>
        <Link to="/account/forgot-password">{t("auth.forgot")}</Link>
      </p>
      <p>
        {t("auth.noAccount")}{" "}
        <Link to="/account/register">{t("nav.register")}</Link>
      </p>
    </section>
  );
}

type ApplyField =
  | "companyName"
  | "orgNumber"
  | "attention"
  | "street"
  | "postcode"
  | "city"
  | "contactEmail";

const APPLY_FIELDS: ApplyField[] = [
  "companyName",
  "orgNumber",
  "attention",
  "street",
  "postcode",
  "city",
  "contactEmail",
];

const EMPTY_APPLICATION: Record<ApplyField, string> = {
  companyName: "",
  orgNumber: "",
  attention: "",
  street: "",
  postcode: "",
  city: "",
  contactEmail: "",
};

/** A text field, or the plan chooser. */
type ApplyProblem = ApplyField | "plan";

/** The fields the browser can already tell are wrong (the server re-checks). */
function applicationProblems(v: Record<ApplyField, string>): ApplyField[] {
  return APPLY_FIELDS.filter((f) => {
    const s = v[f].trim();
    if (!s) return true;
    if (f === "orgNumber") return normaliseOrgNumber(s) === null;
    if (f === "postcode") return normalisePostcode(s) === null;
    if (f === "contactEmail") return !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
    return false;
  });
}

/**
 * "Ansök om konto" - step 1 of the closed registration (2026-10-06, Sigge:
 * "we can't have an open direct registration like this"). An organisation
 * says who it is and where to reach it; nothing can sign in until the
 * provider has reviewed the application in its CMS, and approval mails the
 * contact a link to set a password.
 *
 * Ort fills itself from the postnummer (our own `/postcodes/:code`, bundled
 * GeoNames data) unless the applicant has typed a town of their own.
 *
 * The applicant also picks its plan (ORG-2): the audience size first, then
 * the film levels the provider sells for that size, each with its yearly
 * price. A portal that sells nothing yet takes no applications.
 */
export function ApplyScreen() {
  const { t, lang } = useI18n();
  const { context } = usePortal();
  const [values, setValues] = useState(EMPTY_APPLICATION);
  const [invalid, setInvalid] = useState<ApplyProblem[]>([]);
  const [plans, setPlans] = useState<PlanMatrix | null | "failed">(null);
  const [band, setBand] = useState<AudienceBand | null>(null);
  const [levelId, setLevelId] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    portalApi
      .plans()
      .then((m) => live && setPlans(m))
      .catch(() => live && setPlans("failed"));
    return () => {
      live = false;
    };
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The town we filled in ourselves: replaced on a new postnummer, while a
  // town the applicant typed is left alone.
  const autoCity = useRef<string | null>(null);

  const set = (f: ApplyField, v: string) => {
    setValues((prev) => ({ ...prev, [f]: v }));
    if (invalid.includes(f)) setInvalid((prev) => prev.filter((x) => x !== f));
  };

  const postcode = normalisePostcode(values.postcode);
  useEffect(() => {
    if (!postcode) return;
    let live = true;
    portalApi
      .postcode(postcode)
      .then((r) => {
        if (!live || !r) return;
        setValues((prev) => {
          const mine = prev.city.trim() && prev.city !== autoCity.current;
          if (mine) return prev;
          autoCity.current = r.city;
          return { ...prev, city: r.city };
        });
        setInvalid((prev) => prev.filter((x) => x !== "city"));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [postcode]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const matrix = plans && plans !== "failed" ? plans : null;
    const chosen =
      matrix && band && levelId ? planCell(matrix, band, levelId) : null;
    const problems: ApplyProblem[] = [
      ...(chosen ? [] : (["plan"] as const)),
      ...applicationProblems(values),
    ];
    setInvalid(problems);
    if (problems.length) {
      document.getElementById(`apply-${problems[0]}`)?.focus();
      return;
    }
    setBusy(true);
    try {
      await portalApi.apply({
        companyName: values.companyName.trim(),
        orgNumber: values.orgNumber.trim(),
        attention: values.attention.trim(),
        street: values.street.trim(),
        postcode: values.postcode.trim(),
        city: values.city.trim(),
        contactEmail: values.contactEmail.trim(),
        band: band as AudienceBand,
        levelId: levelId as string,
        locale: lang,
      });
      setSentTo(values.contactEmail.trim());
    } catch (err) {
      const fields = fieldsOf(err);
      if (fields.length) {
        setInvalid(fields);
        document.getElementById(`apply-${fields[0]}`)?.focus();
      } else setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  if (plans === null) return <Loading />;
  const matrix = plans === "failed" ? null : plans;
  if (!matrix || matrix.levels.length === 0) {
    return (
      <section className="panel panel--narrow">
        <PageTitle>{t("apply.title")}</PageTitle>
        <Notice kind={matrix ? "info" : "error"}>
          {matrix
            ? t("apply.closed", { provider: context.portal.name })
            : t("common.error")}
        </Notice>
      </section>
    );
  }

  if (sentTo) {
    return (
      <section className="panel panel--narrow">
        <PageTitle>{t("apply.doneTitle")}</PageTitle>
        <Notice kind="success">
          {t("apply.done", { provider: context.portal.name, email: sentTo })}
        </Notice>
        <p>
          <Link to="/">{t("apply.backToStore")}</Link>
        </p>
      </section>
    );
  }

  const field = (
    f: ApplyField,
    extra: {
      autoComplete: string;
      type?: string;
      inputMode?: "numeric" | "email" | "text";
      hint?: string;
    }
  ) => (
    <Field
      id={`apply-${f}`}
      label={t(`apply.${f}`)}
      hint={extra.hint}
      type={extra.type ?? "text"}
      inputMode={extra.inputMode}
      autoComplete={extra.autoComplete}
      required
      value={values[f]}
      error={invalid.includes(f) ? t(`apply.invalid.${f}`) : null}
      onChange={(e) => set(f, e.target.value)}
    />
  );

  return (
    <section className="panel panel--narrow">
      <PageTitle>{t("apply.title")}</PageTitle>
      <p>{t("apply.intro", { provider: context.portal.name })}</p>
      <form noValidate onSubmit={(e) => void submit(e)}>
        <PlanChooser
          matrix={matrix}
          band={band}
          levelId={levelId}
          invalid={invalid.includes("plan")}
          onBand={(b) => {
            setBand(b);
            if (levelId && !planCell(matrix, b, levelId)) setLevelId(null);
            setInvalid((prev) => prev.filter((x) => x !== "plan"));
          }}
          onLevel={(id) => {
            setLevelId(id);
            setInvalid((prev) => prev.filter((x) => x !== "plan"));
          }}
        />
        {field("companyName", { autoComplete: "organization" })}
        {field("orgNumber", {
          autoComplete: "off",
          hint: t("apply.orgNumberHint"),
        })}
        {field("attention", { autoComplete: "name" })}
        {field("street", { autoComplete: "street-address" })}
        <div className="field-row">
          {field("postcode", {
            autoComplete: "postal-code",
            inputMode: "numeric",
          })}
          {field("city", { autoComplete: "address-level2" })}
        </div>
        {field("contactEmail", {
          autoComplete: "email",
          type: "email",
          inputMode: "email",
        })}
        {error && <Notice kind="error">{error}</Notice>}
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? t("apply.sending") : t("apply.send")}
        </button>
      </form>
      <p>
        {t("auth.haveAccount")} <Link to="/login">{t("nav.login")}</Link>
      </p>
    </section>
  );
}

function fieldsOf(err: unknown): ApplyProblem[] {
  const body =
    err && typeof err === "object" && "body" in err
      ? (err as { body: Record<string, unknown> }).body
      : null;
  const raw = body?.fields;
  return Array.isArray(raw)
    ? (["plan", ...APPLY_FIELDS] as ApplyProblem[]).filter((f) =>
        (raw as unknown[]).includes(f)
      )
    : [];
}

function kr(amount: number, lang: "sv" | "en"): string {
  return new Intl.NumberFormat(lang === "sv" ? "sv-SE" : "en-GB", {
    style: "currency",
    currency: "SEK",
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount);
}

/**
 * The plan cell, as two questions a school can answer: how many watch one
 * screening, then how many films. Only sizes and levels the provider sells
 * are offered; a level not sold for the chosen size is not shown.
 */
function PlanChooser(props: {
  matrix: PlanMatrix;
  band: AudienceBand | null;
  levelId: string | null;
  invalid: boolean;
  onBand(band: AudienceBand): void;
  onLevel(levelId: string): void;
}) {
  const { t, lang } = useI18n();
  const { matrix } = props;
  const bands = AUDIENCE_BANDS.filter((b) =>
    matrix.levels.some((l) => l.prices[b] !== undefined)
  );
  const levels = props.band
    ? matrix.levels.filter((l) => l.prices[props.band!] !== undefined)
    : [];
  const vat = 1 + matrix.vatBps / 10_000;
  return (
    <div className="plan-chooser" id="apply-plan" tabIndex={-1}>
      <fieldset className="choice">
        <legend>{t("apply.band")}</legend>
        <p className="field__hint">{t("apply.bandHint")}</p>
        {bands.map((b) => (
          <label key={b} className="choice__option">
            <input
              type="radio"
              name="apply-band"
              value={b}
              checked={props.band === b}
              onChange={() => props.onBand(b)}
            />
            <span>{bandLabel(b, lang)}</span>
          </label>
        ))}
      </fieldset>
      {props.band && (
        <fieldset className="choice">
          <legend>{t("apply.level")}</legend>
          {levels.map((l) => {
            const price = l.prices[props.band!]!;
            return (
              <label key={l.id} className="choice__option">
                <input
                  type="radio"
                  name="apply-level"
                  value={l.id}
                  checked={props.levelId === l.id}
                  onChange={() => props.onLevel(l.id)}
                />
                <span className="choice__text">
                  <span className="choice__label">
                    {l.label}
                    {l.films === null
                      ? ` · ${t("apply.fullLibrary")}`
                      : ` · ${l.films === 1 ? t("apply.film1") : t("apply.films", { n: l.films })}`}
                  </span>
                  <span className="choice__price">
                    {t("apply.perYear", {
                      price: kr(price, lang),
                      inclVat: kr(Math.round(price * vat * 100) / 100, lang),
                    })}
                  </span>
                </span>
              </label>
            );
          })}
        </fieldset>
      )}
      {props.invalid && (
        <p className="field__error" role="alert">
          {t("apply.invalid.plan")}
        </p>
      )}
    </div>
  );
}

export function ConfirmScreen() {
  const { t } = useI18n();
  const token = useFragmentToken();
  const [state, setState] = useState<"working" | "done" | "bad" | "none">(
    token ? "working" : "none"
  );
  const sent = useRef(false);

  useEffect(() => {
    // Spend the single-use token exactly once, StrictMode's double effect
    // included - a second POST would fail and paint a working link "invalid".
    if (!token || sent.current) return;
    sent.current = true;
    portalApi
      .confirm(token)
      .then(() => setState("done"))
      .catch(() => setState("bad"));
  }, [token]);

  return (
    <section className="panel panel--narrow">
      <PageTitle>{t("auth.confirmTitle")}</PageTitle>
      {state === "working" && (
        <p role="status" className="loading">
          {t("auth.confirming")}
        </p>
      )}
      {state === "done" && (
        <>
          <Notice kind="success">{t("auth.confirmed")}</Notice>
          <Link className="btn btn--primary" to="/login">
            {t("auth.login")}
          </Link>
        </>
      )}
      {(state === "bad" || state === "none") && (
        <>
          <Notice kind="error">
            {state === "bad" ? t("auth.badLink") : t("auth.noToken")}
          </Notice>
          <Link to="/account/resend">{t("auth.resend")}</Link>
        </>
      )}
    </section>
  );
}

function EmailOnlyForm(props: {
  title: string;
  action: string;
  sent: (email: string) => string;
  send: (email: string) => Promise<unknown>;
}) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await props.send(email.trim());
      setSentTo(email.trim());
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel panel--narrow">
      <PageTitle>{props.title}</PageTitle>
      {sentTo ? (
        <Notice kind="success">{props.sent(sentTo)}</Notice>
      ) : (
        <form onSubmit={(e) => void submit(e)}>
          <Field
            label={t("common.email")}
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {error && <Notice kind="error">{error}</Notice>}
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {props.action}
          </button>
        </form>
      )}
      <p>
        <Link to="/login">{t("nav.login")}</Link>
      </p>
    </section>
  );
}

export function ResendScreen() {
  const { t } = useI18n();
  return (
    <EmailOnlyForm
      title={t("auth.resendTitle")}
      action={t("auth.resendSend")}
      sent={(email) => t("auth.resendSent", { email })}
      send={(email) => portalApi.resendConfirmation(email)}
    />
  );
}

export function ForgotScreen() {
  const { t } = useI18n();
  return (
    <EmailOnlyForm
      title={t("auth.forgotTitle")}
      action={t("auth.forgotSend")}
      sent={(email) => t("auth.forgotSent", { email })}
      send={(email) => portalApi.forgotPassword(email)}
    />
  );
}

/** A new password against a fragment token (reset, or a school invite). */
function TokenPasswordForm(props: {
  title: string;
  intro?: string;
  action: string;
  withName?: boolean;
  submit(token: string, password: string, name: string): Promise<string>;
  failure: Partial<Record<string, "auth.badLink" | "auth.inviteGone">>;
}) {
  const { t } = useI18n();
  const token = useFragmentToken();
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const problem = passwordProblem(password);
    if (problem) {
      setError(t(problem));
      return;
    }
    setBusy(true);
    try {
      setDone(await props.submit(token, password, name.trim()));
    } catch (err) {
      setError(errorText(err, t, props.failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="panel panel--narrow">
      <PageTitle>{props.title}</PageTitle>
      {done ? (
        <>
          <Notice kind="success">{done}</Notice>
          <Link className="btn btn--primary" to="/login">
            {t("auth.login")}
          </Link>
        </>
      ) : !token ? (
        <Notice kind="error">{t("auth.noToken")}</Notice>
      ) : (
        <form onSubmit={(e) => void submit(e)}>
          {props.intro && <p>{props.intro}</p>}
          {props.withName && (
            <Field
              label={t("auth.name")}
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}
          <Field
            label={t("auth.newPassword")}
            hint={t("common.passwordHint")}
            type="password"
            autoComplete="new-password"
            required
            minLength={MIN_PASSWORD}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <Notice kind="error">{error}</Notice>}
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {props.action}
          </button>
        </form>
      )}
    </section>
  );
}

export function ResetScreen() {
  const { t } = useI18n();
  const { setMe } = usePortal();
  return (
    <TokenPasswordForm
      title={t("auth.resetTitle")}
      action={t("auth.resetSave")}
      failure={{ invalid_token: "auth.badLink" }}
      submit={async (token, password) => {
        await portalApi.resetPassword(token, password);
        // The reset revoked every session, this browser's included.
        setMe(null);
        return t("auth.resetDone");
      }}
    />
  );
}

/**
 * "Inloggad som X": shown where signing in would otherwise silently keep
 * the wrong person (login, an invitation opened in a signed-in browser).
 * Signing out here leaves for a fresh page (`freshPage`).
 */
export function SignedInAs(props: {
  email: string;
  continueTo?: string;
  switchTo: string;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  return (
    <div className="signed-in-as">
      <Notice>{t("auth.signedInAs", { email: props.email })}</Notice>
      <div className="signed-in-as__actions">
        {props.continueTo && (
          <Link className="btn btn--primary" to={props.continueTo}>
            {t("auth.continueAs")}
          </Link>
        )}
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void portalApi
              .logout()
              .catch(() => undefined)
              .then(() => freshPage(props.switchTo));
          }}
        >
          {t("auth.switchAccount")}
        </button>
      </div>
    </div>
  );
}

export function InviteScreen() {
  const { t } = useI18n();
  const { setLoginEmail, me, setMe } = usePortal();
  return (
    <TokenPasswordForm
      title={t("auth.inviteTitle")}
      intro={t("auth.inviteBody")}
      action={t("auth.inviteSave")}
      withName
      failure={{
        invite_not_found: "auth.inviteGone",
        not_found: "auth.inviteGone",
      }}
      submit={async (token, password, name) => {
        const res = await portalApi.acceptInvite({
          token,
          password,
          name: name || undefined,
        });
        setLoginEmail(res.email);
        // Opened in a browser signed in as someone else (the purchaser
        // trying a viewer's invitation): sign that person out, so the next
        // login cannot land in their account.
        if (me && me.email.toLowerCase() !== res.email.toLowerCase()) {
          await portalApi.logout().catch(() => undefined);
          setMe(null);
          return t("auth.inviteDoneSwitched", {
            email: res.email,
            previous: me.email,
          });
        }
        return t("auth.inviteDone", { email: res.email });
      }}
    />
  );
}
